import { FC, useState } from "react";
import { useNavigate } from "react-router-dom";
import Select from "common/components/base/select/Select";
import {
  knowledgeLibraryItems,
  knowledgeLibraryFilterOptions
} from "modules/knowledge-library/data/config/knowledge-library.config";
import { KnowledgeLibraryItem } from "modules/knowledge-library/data/models/knowledge-library.model";
import { KnowledgeLibraryService } from "modules/knowledge-library/data/services/knowledge-library.service";
import { DocumentCategory } from "modules/knowledge-library/data/enums/document-category.enum";
import {
  Visibility,
  AccessTime,
  RemoveRedEye,
  ShowChart
} from "@mui/icons-material";
import "./KnowledgeLibrary.scss";
import HeaderNew from "common/components/custom/header-new/HeaderNew";
import SearchLoader from "modules/knowledge-library/pages/knowledge-library/components/search-loader/SearchLoader";

const getCategoryClass = (category: DocumentCategory): string => {
  switch (category) {
    case DocumentCategory.CIVIL:
      return "category--civil";
    case DocumentCategory.MECHANICAL:
      return "category--mechanical";
    case DocumentCategory.ELECTRICAL:
      return "category--electrical";
    case DocumentCategory.INSTRUMENTATION:
      return "category--instrumentation";
    default:
      return "";
  }
};

const KnowledgeLibrary: FC = () => {
  const navigate = useNavigate();
  const [asset, setAsset] = useState<string>("Select the Asset");
  const [equipmentType, setEquipmentType] = useState<string>("Select the Type");
  const [discipline, setDiscipline] = useState<string>("Select the Discipline");
  const [items, setItems] = useState<KnowledgeLibraryItem[]>(
    knowledgeLibraryItems
  );
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const isSearchDisabled =
    asset === "Select the Asset" ||
    equipmentType === "Select the Type" ||
    discipline === "Select the Discipline";

  const handleSearch = async () => {
    const request = {
      asset: asset.toLowerCase(),
      equipment_type: equipmentType.toLowerCase(),
      discipline: discipline.toLowerCase()
    };
    setIsLoading(true);
    try {
      const response = await KnowledgeLibraryService.kgFilter(request);
      navigate("/knowledge-library/view-data", {
        state: { kgFilterResponse: response }
      });
    } catch (error) {
      console.error("kg-filter error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Group items into rows of 2
  const rows: KnowledgeLibraryItem[][] = [];
  for (let i = 0; i < items.length; i += 2) {
    rows.push(items.slice(i, i + 2));
  }

  return (
    <>
      {/* <HeaderNew showSearch={false} /> */}
      <div className="knowledge-library">
        <div className="knowledge-library__content">
          <div className="knowledge-library__header">
            <div className="knowledge-library__header-left">
              <div className="knowledge-library__breadcrumb">
                <span
                  className="knowledge-library__breadcrumb-link"
                  onClick={() => navigate("/")}
                >
                  Home
                </span>
                <span className="knowledge-library__breadcrumb-separator">
                  &gt;
                </span>
                <span className="knowledge-library__breadcrumb-current">
                  Knowledge Library
                </span>
              </div>
              <h1 className="knowledge-library__title">Knowledge Library</h1>
            </div>
          </div>

          <div className="knowledge-library__filters">
            <div className="knowledge-library__filter-row">
              <div className="knowledge-library__filter-group">
                <label className="knowledge-library__filter-label">Asset</label>
                <Select
                  options={knowledgeLibraryFilterOptions.assets}
                  value={asset}
                  onChange={(e) => setAsset(e.target.value)}
                  variant="outlined"
                  minWidth={200}
                />
              </div>
              <div className="knowledge-library__filter-group">
                <label className="knowledge-library__filter-label">
                  Equipment Type
                </label>
                <Select
                  options={knowledgeLibraryFilterOptions.equipmentTypes}
                  value={equipmentType}
                  onChange={(e) => setEquipmentType(e.target.value)}
                  variant="outlined"
                  minWidth={200}
                />
              </div>
              <div className="knowledge-library__filter-group">
                <label className="knowledge-library__filter-label">
                  Discipline
                </label>
                <Select
                  options={knowledgeLibraryFilterOptions.disciplines}
                  value={discipline}
                  onChange={(e) => setDiscipline(e.target.value)}
                  variant="outlined"
                  minWidth={200}
                />
              </div>
              <SearchLoader
                className="knowledge-library__search-btn"
                onClick={handleSearch}
                disabled={isSearchDisabled}
                loading={isLoading}
              >
                Search
              </SearchLoader>
            </div>
          </div>

          <h2 className="knowledge-library__recent-search">Recent Search</h2>

          <div className="knowledge-library__grid">
            {rows.map((row, rowIndex) => (
              <div key={rowIndex} className="knowledge-library__grid-row">
                {row.map((item) => (
                  <div key={item.id} className="knowledge-library__card">
                    <div className="knowledge-library__card-content">
                      <div className="knowledge-library__card-container">
                        <div className="knowledge-library__card-header">
                          <span
                            className={`knowledge-library__card-category ${getCategoryClass(
                              item.category
                            )}`}
                          >
                            {item.category}
                          </span>
                          {/* <span className="knowledge-library__card-version">
                            {item.version}
                          </span> */}
                        </div>

                        <div className="knowledge-library__card-info">
                          <h3 className="knowledge-library__card-title">
                            {item.title}
                          </h3>
                          <p className="knowledge-library__card-description">
                            {item.description}
                          </p>
                        </div>

                        <div className="knowledge-library__card-tags">
                          {item.tags.map((tag) => (
                            <span
                              key={tag}
                              className="knowledge-library__card-tag"
                            >
                              {tag}
                            </span>
                          ))}
                        </div>
                      </div>

                      <div className="knowledge-library__card-bottom">
                        {/* <div className="knowledge-library__card-actions">
                          <Button
                            className="knowledge-library__card-btn knowledge-library__card-btn--view-data"
                            onClick={() => {}}
                          >
                            <svg
                              width="20"
                              height="20"
                              viewBox="129 87 22 16"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                fillRule="evenodd"
                                clipRule="evenodd"
                                d="M148.316 97.3078C144.982 89.8974 135.019 89.8974 131.684 97.3078C131.514 97.6855 131.07 97.8539 130.692 97.6839C130.315 97.514 130.146 97.07 130.316 96.6922C134.182 88.1026 145.819 88.1026 149.684 96.6922C149.854 97.07 149.686 97.514 149.308 97.6839C148.93 97.8539 148.486 97.6855 148.316 97.3078Z"
                                fill="currentColor"
                              />
                              <path
                                fillRule="evenodd"
                                clipRule="evenodd"
                                d="M140 95.75C141.243 95.75 142.25 96.7574 142.25 98C142.25 99.2426 141.243 100.25 140 100.25C138.758 100.25 137.75 99.2426 137.75 98C137.75 96.7574 138.758 95.75 140 95.75ZM143.75 98C143.75 95.9289 142.071 94.25 140 94.25C137.929 94.25 136.25 95.9289 136.25 98C136.25 100.071 137.929 101.75 140 101.75C142.071 101.75 143.75 100.071 143.75 98Z"
                                fill="currentColor"
                              />
                            </svg>
                            View Data
                          </Button>
                          <Button
                            className="knowledge-library__card-btn knowledge-library__card-btn--view-graph"
                            onClick={() => {}}
                          >
                            <ShowChart />
                            View graph
                          </Button>
                        </div> */}

                        <div className="knowledge-library__card-divider" />

                        <div className="knowledge-library__card-footer">
                          <span className="knowledge-library__card-meta">
                            <AccessTime />
                            {item.timeAgo}
                          </span>
                          <span className="knowledge-library__card-meta knowledge-library__card-meta--views">
                            <Visibility />
                            {item.views} views
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
};

export default KnowledgeLibrary;
